import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Table, Form, Spinner, Alert } from "react-bootstrap";
import api from "../../utils/ApiConfig";

const STATUT_CONFIG = {
  "accepté":  { bg: "success",   label: "✅ Accepté" },
  "rejeté":   { bg: "danger",    label: "❌ Rejeté" },
  "converti": { bg: "warning",   label: "🔄 Converti" },
};

export default function UploadDiagnosticsScreen() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [jours, setJours]   = useState(30);

  useEffect(() => {
    setLoading(true);
    api.get(`/bo/upload-diagnostics?jours=${jours}`)
      .then(r => setData(r.data))
      .catch(() => setError("Erreur de chargement."))
      .finally(() => setLoading(false));
  }, [jours]);

  if (loading) return <Container className="py-5 text-center"><Spinner animation="border" variant="primary" /></Container>;
  if (error) return <Container><Alert variant="danger">{error}</Alert></Container>;

  const { kpis, par_extension, par_plateforme, top_erreurs, derniers_rejets } = data;

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">📎 Diagnostic uploads fichiers</h4>
          <small className="text-muted">Analyse des tentatives d'upload sur Marion Livraison</small>
        </Col>
        <Col xs="auto">
          <Form.Select size="sm" value={jours} onChange={e => setJours(e.target.value)}>
            <option value={7}>7 derniers jours</option>
            <option value={30}>30 derniers jours</option>
            <option value={90}>90 derniers jours</option>
          </Form.Select>
        </Col>
      </Row>

      {/* KPIs */}
      <Row className="mb-4 g-3">
        {[
          { label: "Total tentatives", value: kpis.total, color: "#6b7280" },
          { label: "✅ Acceptés", value: kpis.acceptes, color: "#16a34a" },
          { label: "🔄 Convertis", value: kpis.convertis, color: "#ca8a04" },
          { label: "❌ Rejetés", value: kpis.rejetes, color: "#dc2626" },
          { label: "Taux de succès", value: kpis.taux_succes + "%", color: kpis.taux_succes >= 90 ? "#16a34a" : kpis.taux_succes >= 70 ? "#ca8a04" : "#dc2626" },
        ].map((k, i) => (
          <Col key={i} xs={6} md={2}>
            <Card className="border-0 shadow-sm text-center h-100">
              <Card.Body>
                <div style={{ fontSize: 26, fontWeight: 800, color: k.color }}>{k.value}</div>
                <div className="text-muted small mt-1">{k.label}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-4 mb-4">
        {/* Par extension */}
        <Col md={8}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <h6 className="fw-semibold mb-3">Par extension de fichier</h6>
              <Table hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Extension</th>
                    <th className="text-center">Total</th>
                    <th className="text-center">✅</th>
                    <th className="text-center">🔄</th>
                    <th className="text-center">❌</th>
                    <th className="text-center">Poids moy.</th>
                    <th className="text-center">Poids max</th>
                  </tr>
                </thead>
                <tbody>
                  {par_extension.map((r, i) => (
                    <tr key={i} style={{ backgroundColor: r.rejetes > 0 && r.rejetes === r.total ? "#fff5f5" : undefined }}>
                      <td>
                        <Badge bg={r.rejetes === r.total ? "danger" : r.convertis > 0 ? "warning" : "success"} className="text-uppercase">
                          {r.extension || "inconnu"}
                        </Badge>
                      </td>
                      <td className="text-center fw-bold">{r.total}</td>
                      <td className="text-center text-success">{r.acceptes}</td>
                      <td className="text-center text-warning">{r.convertis}</td>
                      <td className="text-center text-danger">{r.rejetes}</td>
                      <td className="text-center text-muted small">{r.taille_moy} Mo</td>
                      <td className="text-center text-muted small">{r.taille_max} Mo</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Par plateforme + top erreurs */}
        <Col md={4}>
          <Card className="border-0 shadow-sm mb-3">
            <Card.Body>
              <h6 className="fw-semibold mb-3">Par plateforme</h6>
              {par_plateforme.map((p, i) => (
                <div key={i} className="d-flex justify-content-between mb-1">
                  <span className="small">
                    {p.platform === 'ios' ? '🍎 iOS' : p.platform === 'android' ? '🤖 Android' : '💻 Web'}
                  </span>
                  <span className="small">
                    {p.total} uploads
                    {p.rejetes > 0 && <span className="text-danger ms-1">({p.rejetes} rejetés)</span>}
                  </span>
                </div>
              ))}
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h6 className="fw-semibold mb-3">Top erreurs</h6>
              {top_erreurs.length === 0 ? (
                <p className="text-muted small mb-0">Aucune erreur sur la période.</p>
              ) : top_erreurs.map((e, i) => (
                <div key={i} className="mb-2">
                  <div className="d-flex justify-content-between">
                    <Badge bg="danger" className="fw-normal">{e.nb}x</Badge>
                  </div>
                  <div className="small text-muted mt-1" style={{ wordBreak: "break-word" }}>{e.erreur}</div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Derniers rejets */}
      {derniers_rejets.length > 0 && (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <h6 className="fw-semibold mb-3">Derniers rejets</h6>
            <Table hover size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Fichier</th>
                  <th>Extension</th>
                  <th>Poids</th>
                  <th>Plateforme</th>
                  <th>Erreur</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {derniers_rejets.map((r, i) => (
                  <tr key={i}>
                    <td className="small text-muted">{r.nom}</td>
                    <td><Badge bg="secondary" className="text-uppercase">{r.extension}</Badge></td>
                    <td className="small">{r.taille_mo} Mo</td>
                    <td className="small">{r.platform}</td>
                    <td className="small text-danger">{r.erreur}</td>
                    <td className="small text-muted">
                      {new Date(r.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
